import { useTranslation } from "react-i18next";
import { FormErrors, ParticipantCampaignDto, ParticipantCampaignSurveyListForm, ParticipantFormData } from "../types";
import { useEffect, useRef, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import ParticipantPersonForm from "../components/FormSections/ParticipantPersonForm";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { createParticipant, getParticipantDetails, updateParticipant } from "../api/participantsAPI";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { validateEmail, validatePhone } from "../../../../utils/validators/FormValidators";
import { convertUtc } from "../../../../utils/dateUtils"
import ParticipantOrganizationalForm from "../components/FormSections/ParticipantOrganizationalForm";
import ParticipantCampaignsForm from "../components/FormSections/ParticipantCampaignsForm";
import { getParticipantCampaigns } from "../../campaign/api/campaignAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { useNavigationGuard } from "../../../../contexts/NavigationGuardContext";

const INITIAL_FORM_DATA: ParticipantFormData = {
    externApplication: '',
    nameParticipant: '',
    surnameParticipant: '',
    surname2Participant: '',
    phone: '',
    email: '',
    birthDate: null,
    bithCountry: '',
    gender: '',
    zipCode: '',
    city: '',
    province: '',
    region: '',
    country: '',
    creationDate: null,
    actDate: null,
    codeAct: '',
    act: '',
    codeCenter: '',
    nameCenter: '',
    codeService: '',
    nameService: '',
    codeUnit: '',
    nameUnit: '',
    location: '',
    building: '',
    surveyCampaigns: []
};

const INITIAL_ERRORS: FormErrors = {};

type ParticipantFormLocationState = {
    campaignId?: number;
    campaignName?: string;
    surveyId?: number;
    surveyName?: string;
    fromSurveyList?: boolean;
};

export default function ParticipantForm() {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const location = useLocation();
    const initialState = (location.state as ParticipantFormLocationState | null) ?? {};
    const isFromSurveyList = initialState.fromSurveyList === true;

    const [formData, setFormData] = useState<ParticipantFormData>(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState<FormErrors>(INITIAL_ERRORS);
    const [campaignOptions, setCampaignOptions] = useState<ParticipantCampaignDto[]>([]);

    const { participantId } = useParams<{ participantId: string }>();
    const { setNavigationGuard, navigateWithGuard } = useNavigationGuard();
    const initialParticipantSnapshot = useRef<string | null>(null);
    // Re-registers the guard when a save launched from the modal fails,
    // so the pending changes stay protected.
    const [guardVersion, setGuardVersion] = useState(0);
    
    const fetchCampaigns = async () => {
        try {
            setCampaignOptions(await getParticipantCampaigns());            
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError.message,
                type: 'danger',
                duration: 8000
            });
        }
    };

    const fetchParticipant = async () => {
        if (participantId) {
            try {
                const data = await getParticipantDetails(participantId); 
                data.birthDateISO = data.birthDate ? new Date(data.birthDate): null;
                data.actDateISO = data.actDate ? new Date(data.actDate) : null;

                setFormData(data);
                initialParticipantSnapshot.current = JSON.stringify(data);
            } catch (error) {
                console.error("Error fetching client details:", error);                    
            }
        }
    };
    
    useEffect(() => {
        if (!participantId) {
            initialParticipantSnapshot.current = JSON.stringify(INITIAL_FORM_DATA);
        }
        fetchParticipant();
        fetchCampaigns();
    }, [participantId]);

    useEffect(() => {
        const hasChanges = !!initialParticipantSnapshot.current &&
            JSON.stringify(formData) !== initialParticipantSnapshot.current;

        setNavigationGuard({
            enabled: hasChanges,
            message: t("participant:modal.modalExitWithUnsavedParticipant"),
            messageButtonSave: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonSave"),
            messageButtonClose: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonClose"),
            onSave: async (redirectTo, redirectState) => {
                return await submitForm(redirectTo, redirectState);
            }
        });

        return () => {
            setNavigationGuard({
                enabled: false,
                message: "",
                messageButtonSave: "",
                messageButtonClose: ""
            });
        };
    }, [formData, guardVersion, setNavigationGuard, t]);
    
    const changeInputField = (field: keyof ParticipantFormData) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setFormData({
            ...formData,
            [field]: e.target.value
        });
    };
    
    const changeDataField = (field: keyof ParticipantFormData) => (date: Date | null) => {
        setFormData({
            ...formData,
            [field]: date
        });
    };

    const buildCreateParticipantPayload = (): ParticipantFormData => {
        const payload: ParticipantFormData = {
            ...formData,
            surveyCampaigns: [...formData.surveyCampaigns],
        };

        if (!initialState.campaignId) {
            return payload;
        }

        const existingCampaign = payload.surveyCampaigns.find(
            (campaign) =>
                campaign.campaignId === initialState.campaignId &&
                (initialState.surveyId != null ? campaign.surveyId === initialState.surveyId : campaign.surveyId == null)
        );

        if (existingCampaign) {
            return payload;
        }

        const selectedCampaign = campaignOptions.find((item) => item.id === initialState.campaignId);

        payload.surveyCampaigns = [
            ...payload.surveyCampaigns,
            {
                id: 0,
                campaignId: initialState.campaignId,
                campaignName: selectedCampaign?.name ?? initialState.campaignName ?? "",
                campaignStatus: selectedCampaign?.status,
                surveyId: initialState.surveyId,
                surveyName: initialState.surveyName,
                surveyStatus: undefined,
                dataBaixa: null,
            },
        ];

        return payload;
    };

    const submitForm = async (redirectTo?: string, redirectState?: unknown): Promise<boolean> => {
        const newErrors: { phone?: string, email?: string, camping?: string } = {};
        // if (formData.camping == "-1") {
        //     newErrors.camping = t("common:form.requiredFieldForm");
        // }

        const emailError = validateEmail(formData.email, t);
        if (emailError) {
            newErrors.email = emailError;
        }

        // Validate phone
        const phoneError = validatePhone(formData.phone, t);
        if (phoneError) {
            newErrors.phone = phoneError;
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setGuardVersion((version) => version + 1);
            return false;
        }
        try {
            if (formData.birthDateISO) {
                formData.birthDate = convertUtc(formData.birthDateISO)?.getTime() ?? null;
            }
            if (formData.actDateISO) {
                formData.actDate = convertUtc(formData.actDateISO)?.getTime() ?? null;
            }
            try {
                if (participantId) {
                    await updateParticipant(participantId!, formData);
                } else {
                    await createParticipant(buildCreateParticipantPayload());
                }
            } catch (error) {
                const saveError = error as ApiErrorResponse;
                showAlert({
                    message: saveError?.message ?? t("participant:saveErrorForm"),
                    type: 'danger',
                    duration: 8000
                });
                setGuardVersion((version) => version + 1);
                return false;
            }

            initialParticipantSnapshot.current = JSON.stringify(formData);
            onSaveSuccessfully(redirectTo, redirectState);
            return true;
        } catch {
            showAlert({
                message: t("participant:saveErrorForm"),
                type: 'danger',
                duration: 8000
            });
            setGuardVersion((version) => version + 1);
            return false;
        }
    }

    const onSaveSuccessfully = (redirectTo?: string, redirectState?: unknown) => {
        const defaultRedirectTo = isFromSurveyList && initialState.surveyId
            ? `/admin/surveyList/${initialState.surveyId}`
            : '/admin/participants';

        const defaultRedirectState = {
            message: t("participant:saveSuccessForm"),
            ...(isFromSurveyList && initialState.surveyId && { tab: 'participants', shouldRefresh: true })
        };

        navigate(
            redirectTo ?? defaultRedirectTo,
            {
                state: redirectState
                    ? { ...defaultRedirectState, ...(redirectState as object) }
                    : defaultRedirectState
            }
        );
    }

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const addCampaign = (newCampaign: ParticipantCampaignSurveyListForm) => {
        const exists = formData.surveyCampaigns.some(c => 
            c.campaignId === newCampaign.campaignId && 
            c.surveyId === newCampaign.surveyId
        );

        let updatedList;

        if (exists) {
            updatedList = formData.surveyCampaigns.map(c => {
                if (c.campaignId === newCampaign.campaignId && c.surveyId === newCampaign.surveyId) {
                    return { ...c, dataBaixa: null }; 
                }
                return c;
            });
        } else {
            updatedList = [...formData.surveyCampaigns, { ...newCampaign, dataBaixa: null }];
        }

        setFormData(prev => ({
            ...prev,
            surveyCampaigns: updatedList
        }));
    };

    const deleteCampign = (surveyCampaign: ParticipantCampaignSurveyListForm) => {
        let updatedSurveyCampaigns = formData.surveyCampaigns;
        if (participantId) {
            updatedSurveyCampaigns = formData.surveyCampaigns.map(participantCampaign => {
               
                const isTarget =
                    participantCampaign.dataBaixa == null
                    && (surveyCampaign.surveyId != null ? surveyCampaign.surveyId === participantCampaign.surveyId : participantCampaign.surveyId == null)
                    && surveyCampaign.campaignId === participantCampaign.campaignId;
                if (isTarget) {
                    return {
                        ...participantCampaign,
                        dataBaixa: new Date()
                    };
                }
                return participantCampaign;
            });
        } else {
            const campaignToDelete = formData.surveyCampaigns.findIndex(participantCampaign => 
                (surveyCampaign.surveyId ? surveyCampaign.surveyId === participantCampaign.surveyId : true)
                && surveyCampaign.campaignId === participantCampaign.campaignId)
            
            updatedSurveyCampaigns.splice(campaignToDelete, 1);
        }

        setFormData({
            ...formData,
            surveyCampaigns: updatedSurveyCampaigns
        });
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>
                        {participantId
                            ? t("participant:updateParticipant")
                            : t("participant:newParticipant")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => submitForm()}
                        onCancelClick={() =>
                        navigateWithGuard(
                            isFromSurveyList && initialState.surveyId
                            ? `/admin/surveyList/${initialState.surveyId}`
                            : "/admin/participants",
                            isFromSurveyList && initialState.surveyId
                            ? { state: { tab: "participants" } }
                            : undefined
                        )
                        }
                        cancelLabel={
                            participantId ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")
                        }
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Form>
                <ParticipantPersonForm
                    formData={formData}
                    errors={errors}
                    onChangeInputField={changeInputField}
                    onChangeDataField={changeDataField}
                />
                <ParticipantOrganizationalForm
                    formData={formData}
                    onChangeInputField={changeInputField}
                    onChangeDataField={changeDataField}
                />
                {participantId && (
                    <ParticipantCampaignsForm
                        selectedCampaigns={formData.surveyCampaigns}
                        campaignOptions={campaignOptions}
                        onAddCampaign={addCampaign}
                        onDeleteCampaign={deleteCampign}
                    />
                )}
            </Form>
        </>
    );

}

